import React from 'react';
import styled from 'styled-components';
import {
  screenMdMin,
  spacer16,
  heading1FontSizeXSmall,
  spacer4,
  spacer8,
} from '@spotify-internal/encore-foundation';
import { IconArrowRight, Type } from '@spotify-internal/encore-web';

import { hexToRgba } from '@mrkt/features/color-conversion';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { ContentfulAsset } from '../../Contentful';
import {
  ButtonTertiaryAsLink,
  DELAYS,
  ImageAspectBox1x1,
  withAnimationContext,
} from '../../ui';
import { fixHref } from '../../utils';
import { fadeInAnimation } from '../../ui/Animations';

type ThreeUpSegmentType = {
  headline?: string;
  body?: string;
  linkText?: string;
  linkUrl?: string;
  image?: ContentfulAsset;
};

const Segment = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
`;

const ImageBox = styled(ImageAspectBox1x1)`
  width: 100%;
  padding: 0px ${spacer16};

  @media (max-width: ${screenMdMin}) {
    width: 60%;
    align-self: center;
  }
`;

const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    variant: Type.heading2,
    weight: Type.bold,
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
    font-size: ${heading1FontSizeXSmall};
    padding-top: ${spacer16};
    padding-bottom: ${spacer16};

    @media (max-width: ${screenMdMin}) {
      padding-top: ${spacer16};
    }
  `,
);

const SegmentTextLink = styled(ButtonTertiaryAsLink)`
  padding: 0px;
  text-align: center;

  color: ${({ theme }) => theme.box.text};
  text-decoration: none;

  &:hover:not(:focus):not(:disabled) {
    color: ${({ theme }) => hexToRgba(theme.box.text, 0.5)};
  }

  svg {
    padding-left: ${spacer8};
    padding-bottom: ${spacer4};
    vertical-align: middle;
  }
`;

const BodySection = withAnimationContext(
  styled(Type.p).attrs({
    variant: Type.body1,
  })`
    ${fadeInAnimation(DELAYS.FOURTH)};
  `,
);

type ThreeUpSegmentParams = {
  threeUpSegment: ThreeUpSegmentType;
};

const TRACK_CAT = 'module-featuredContent3Up';

export const ThreeUpSegment: React.FC<ThreeUpSegmentParams> = ({
  threeUpSegment = {},
}) => {
  return (
    <Segment>
      {threeUpSegment.image && (
        <ImageBox>
          <ContentfulImage asset={threeUpSegment.image} />
        </ImageBox>
      )}
      <Headline>{threeUpSegment.headline}</Headline>
      <BodySection>{threeUpSegment.body}</BodySection>
      {threeUpSegment.linkText && threeUpSegment.linkUrl && (
        <SegmentTextLink
          href={fixHref(threeUpSegment.linkUrl)}
          track={[TRACK_CAT, `Link - ${threeUpSegment.linkText}`]}
        >
          {threeUpSegment.linkText}
          <IconArrowRight />
        </SegmentTextLink>
      )}
    </Segment>
  );
};
